'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { Eye, EyeOff, Loader2, Sparkles, Palette, Zap, Building2, Heart, Scissors, Brush, Droplets, Flower2, Hand, Mail, ArrowRight, RefreshCw, ExternalLink, ShieldCheck,  } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import { PROFESSIONS, type ProfessionId } from '@/lib/professions';

interface SignUpFormValues {
  fullName: string;
  email: string;
  password: string;
  userType: string;
  terms: boolean;
}

const iconMap: Record<string, any> = {
  lash: Sparkles,
  esthe: Palette,
  brow: Zap,
  nail: Brush,
  makeup: Sparkles,
  hair: Scissors,
  wax: Droplets,
  spa: Flower2,
  massage: Hand,
  studio: Building2,
  client: Heart,
};

const professions = PROFESSIONS.map((p) => ({
  id: p.id,
  label: p.brandLabel,
  sublabel: p.specialty,
  icon: iconMap[p.id] || Sparkles,
  color: p.color,
  userType: p.userType,
}));

interface SignUpFormProps {
  onSwitchToSignIn: () => void;
  defaultRole?: string;
}

export default function SignUpForm({ onSwitchToSignIn, defaultRole }: SignUpFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [verificationPending, setVerificationPending] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [registeredUserType, setRegisteredUserType] = useState<string>('professional');
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const router = useRouter();
  const { signUp, resendVerificationEmail } = useAuth();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<SignUpFormValues>({
    defaultValues: { userType: defaultRole || '' },
  });

  const selectedType = watch('userType');

  const handleResend = async () => {
    if (!registeredEmail || resending || resendCooldown > 0) return;
    setResending(true);
    try {
      if (resendVerificationEmail) {
        await resendVerificationEmail(registeredEmail);
      } else {
        const supabase = createClient();
        await supabase.auth.resend({
          type: 'signup',
          email: registeredEmail,
        });
      }
      toast.success('Verification email resent!', {
        description: `Check your inbox at ${registeredEmail}`,
      });
      setResendCooldown(60);
      const timer = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err: any) {
      toast.error('Could not resend verification email', {
        description: err?.message || 'Please try again in a few moments.',
      });
    } finally {
      setResending(false);
    }
  };

  const handleContinueAfterVerification = () => {
    if (registeredUserType === 'client') {
      router.push('/client-portal');
    } else {
      router.push('/onboarding-screen');
    }
  };

  const onSubmit = async (data: SignUpFormValues) => {
    setIsLoading(true);
    const selectedProf = professions.find((p) => p.id === data.userType);
    const userRoleType = selectedProf?.userType || 'professional';
    try {
      const result = await signUp(data.email.trim().toLowerCase(), data.password, {
        fullName: data.fullName,
        profession: data.userType as ProfessionId,
        userType: userRoleType,
        role: userRoleType === 'client' ? 'client' : 'professional',
      });

      // If professional, start trial after signup
      if (userRoleType === 'professional' && result?.user) {
        try {
          const supabase = createClient();
          await supabase.rpc('start_professional_trial', { p_user_id: result.user.id });
        } catch {
          // Trial start failure is non-blocking
        }
      }

      // Welcome email via Edge Function templates + Resend (no Auth SMTP)
      try {
        const { sendAppEmail } = await import('@/lib/sendAppEmail');
        const template = userRoleType === 'client' ? 'welcome_client' : 'welcome_professional';
        await sendAppEmail({
          template,
          to: data.email.trim().toLowerCase(),
          data: { name: data.fullName },
        });
      } catch {
        // Email failure is non-blocking
      }

      // If session exists immediately (email confirmation disabled or auto-confirmed)
      if (result?.session && result?.user) {
        toast.success('Account created successfully!');
        if (userRoleType === 'client') {
          router.push('/client-portal');
        } else {
          router.push('/onboarding-screen');
        }
        return;
      }

      setRegisteredEmail(data.email.trim().toLowerCase());
      setRegisteredUserType(userRoleType);
      setVerificationPending(true);

      toast.success('Account created! Please verify your email.', {
        description: `We've sent a verification link to ${data.email}.`,
      });
    } catch (err: any) {
      setIsLoading(false);
      const msg = String(err?.message || '').toLowerCase();
      if (
        err?.code === 'email_exists' ||
        msg.includes('already exists') ||
        msg.includes('already registered')
      ) {
        toast.error('Email already registered', {
          description: 'An account with this email already exists. Sign in instead.',
          action: { label: 'Sign in', onClick: onSwitchToSignIn },
        });
      } else if (msg.includes('failed to fetch') || msg.includes('networkerror')) {
        toast.error('Connection issue', {
          description: 'Unable to reach the authentication service. Please check your connection and try again.',
        });
      } else {
        toast.error('Sign up failed', { description: err?.message || 'Please try again.' });
      }
    }
  };

  if (verificationPending) {
    return (
      <div className="animate-fade-in text-center py-4">
        <div
          className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center shadow-lg"
          style={{ background: 'rgba(158,148,206,0.15)', border: '1px solid rgba(158,148,206,0.3)' }}
        >
          <Mail size={32} style={{ color: '#9E94CE' }} />
        </div>

        <h2 className="text-2xl font-bold text-foreground mb-2">Verify your email</h2>
        <p className="text-muted-foreground text-sm max-w-md mx-auto mb-4">
          We&apos;ve sent a verification link to{' '}
          <strong className="text-foreground font-semibold">{registeredEmail}</strong>. Please check
          your inbox and click the link to confirm your account.
        </p>

        <div className="p-4 rounded-xl mb-6 text-left text-xs text-muted-foreground bg-muted/50 border border-border">
          <div className="flex items-center gap-2 text-foreground font-semibold mb-1">
            <ShieldCheck size={14} className="text-primary" />
            Quick Tips:
          </div>
          <ul className="list-disc pl-4 space-y-1">
            <li>If you don&apos;t see the email within 1-2 minutes, check your spam/junk folder.</li>
            <li>Clicking the verification link automatically confirms your AvenLink profile.</li>
          </ul>
        </div>

        {/* Webmail Quick Links */}
        <div className="grid grid-cols-2 gap-2.5 mb-6">
          <a
            href="https://mail.google.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-xl text-xs font-semibold border border-border bg-card hover:bg-muted transition-colors text-foreground"
          >
            Open Gmail <ExternalLink size={12} className="text-muted-foreground" />
          </a>
          <a
            href="https://outlook.live.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-xl text-xs font-semibold border border-border bg-card hover:bg-muted transition-colors text-foreground"
          >
            Open Outlook <ExternalLink size={12} className="text-muted-foreground" />
          </a>
        </div>

        {/* Resend and Continue Buttons */}
        <div className="space-y-3">
          <button
            onClick={handleContinueAfterVerification}
            className="btn-primary w-full py-3 text-sm font-semibold flex items-center justify-center gap-2"
          >
            I&apos;ve verified my email — Continue
            <ArrowRight size={16} />
          </button>

          <button
            onClick={handleResend}
            disabled={resending || resendCooldown > 0}
            className="w-full py-2.5 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50"
          >
            {resending ? (
              <>
                <RefreshCw size={13} className="animate-spin" /> Resending...
              </>
            ) : resendCooldown > 0 ? (
              `Resend email in ${resendCooldown}s`
            ) : (
              <>
                <RefreshCw size={13} /> Didn&apos;t get the email? Resend
              </>
            )}
          </button>

          <div className="pt-2">
            <button
              onClick={() => setVerificationPending(false)}
              className="text-xs text-muted-foreground hover:underline"
            >
              Entered wrong email? Back to sign up
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-1">Create your account</h2>
        <p className="text-muted-foreground text-sm">
          Join AvenLink — the beauty industry platform
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5">
        {/* Role selector */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            I am a <span className="text-danger">*</span>
          </label>
          <input
            type="hidden"
            {...register('userType', { required: 'Select your role to continue' })}
          />
          <div className="grid grid-cols-4 gap-2">
            {professions.map((p) => {
              const PIcon = p.icon;
              const isActive = selectedType === p.id;
              return (
                <button
                  key={`prof-${p.id}`}
                  type="button"
                  onClick={() => setValue('userType', p.id, { shouldValidate: true })}
                  className="p-2.5 rounded-xl text-center transition-all duration-200 cursor-pointer relative"
                  style={{
                    background: isActive ? `${p.color}12` : 'var(--muted)',
                    border: isActive ? `1.5px solid ${p.color}50` : '1.5px solid transparent',
                    boxShadow: isActive ? `0 0 0 1px ${p.color}20` : 'none',
                  }}
                >
                  <PIcon size={14} style={{ color: p.color }} className="mx-auto mb-1" />
                  <div
                    className="text-xs font-semibold leading-tight"
                    style={{
                      color: isActive ? p.color : 'var(--muted-foreground)',
                      fontSize: '10px',
                    }}
                  >
                    {p.sublabel}
                  </div>
                  {isActive && (
                    <div
                      className="absolute top-1 right-1 w-3 h-3 rounded-full flex items-center justify-center"
                      style={{ background: p.color }}
                    >
                      <svg width="6" height="6" viewBox="0 0 8 8" fill="none">
                        <path
                          d="M1.5 4L3 5.5L6.5 2"
                          stroke="#fff"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          {errors.userType && (
            <p className="text-danger text-xs mt-1.5 font-medium">{errors.userType.message}</p>
          )}
          {selectedType && selectedType !== 'client' && (
            <div
              className="mt-2 px-3 py-2 rounded-lg text-xs font-medium flex items-center gap-2"
              style={{
                background: 'rgba(139,124,248,0.06)',
                color: '#6d5ce8',
                border: '1px solid rgba(139,124,248,0.15)',
              }}
            >
              <Sparkles size={11} />
              30-day Pro trial included — no credit card required
            </div>
          )}
          {selectedType === 'client' && (
            <div
              className="mt-2 px-3 py-2 rounded-lg text-xs font-medium"
              style={{
                background: 'rgba(244,63,94,0.06)',
                color: '#e11d48',
                border: '1px solid rgba(244,63,94,0.15)',
              }}
            >
              Free to join — search and book beauty professionals
            </div>
          )}
        </div>

        {/* Full name */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5" htmlFor="signup-name">
            Full name <span className="text-danger">*</span>
          </label>
          <input
            id="signup-name"
            type="text"
            autoComplete="name"
            placeholder="Your name"
            className="input-base w-full px-4 py-3 text-sm"
            {...register('fullName', {
              required: 'Full name is required',
              minLength: { value: 2, message: 'Name must be at least 2 characters' },
            })}
          />
          {errors.fullName && (
            <p className="text-danger text-xs mt-1.5 font-medium">{errors.fullName.message}</p>
          )}
        </div>

        {/* Email */}
        <div>
          <label
            className="block text-sm font-medium text-foreground mb-1.5"
            htmlFor="signup-email"
          >
            Email address <span className="text-danger">*</span>
          </label>
          <input
            id="signup-email"
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            className="input-base w-full px-4 py-3 text-sm"
            {...register('email', {
              required: 'Email is required',
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: 'Enter a valid email address',
              },
            })}
          />
          {errors.email && (
            <p className="text-danger text-xs mt-1.5 font-medium">{errors.email.message}</p>
          )}
        </div>

        {/* Password */}
        <div>
          <label
            className="block text-sm font-medium text-foreground mb-1.5"
            htmlFor="signup-password"
          >
            Password <span className="text-danger">*</span>
          </label>
          <div className="relative">
            <input
              id="signup-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Min. 8 characters"
              className="input-base w-full px-4 py-3 text-sm pr-11"
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 8, message: 'Password must be at least 8 characters' },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && (
            <p className="text-danger text-xs mt-1.5 font-medium">{errors.password.message}</p>
          )}
        </div>

        {/* Terms */}
        <div>
          <div className="flex items-start gap-2.5">
            <input
              id="terms"
              type="checkbox"
              className="w-4 h-4 mt-0.5 rounded border-border flex-shrink-0"
              style={{ accentColor: 'var(--primary)' }}
              {...register('terms', { required: 'You must accept the terms to continue' })}
            />
            <label htmlFor="terms" className="text-xs text-muted-foreground leading-relaxed">
              I agree to AvenLink&apos;s{' '}
              <Link
                href="/terms-of-service"
                target="_blank"
                className="font-medium hover:text-foreground cursor-pointer transition-colors"
                style={{ color: 'var(--primary)' }}
              >
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link
                href="/privacy-policy"
                target="_blank"
                className="font-medium hover:text-foreground cursor-pointer transition-colors"
                style={{ color: 'var(--primary)' }}
              >
                Privacy Policy
              </Link>
            </label>
          </div>
          {errors.terms && (
            <p className="text-danger text-xs mt-1.5 font-medium">{errors.terms.message}</p>
          )}
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="btn-primary w-full py-3 text-sm font-semibold flex items-center justify-center gap-2"
          style={{ minHeight: '48px' }}
        >
          {isLoading ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Creating account...
            </>
          ) : (
            'Create AvenLink Account'
          )}
        </button>
      </form>

      <p className="text-center text-sm text-muted-foreground mt-6">
        Already have an account?{' '}
        <button
          onClick={onSwitchToSignIn}
          className="font-semibold hover:text-foreground transition-colors"
          style={{ color: 'var(--primary)' }}
        >
          Sign in
        </button>
      </p>
    </div>
  );
}
